import useLazyWorkspaceUrl from '@hooks/use-lazy-workspace-url';
import { NavItems } from '@hooks/use-navigation-context';
import useWorkspaceUrl from '@hooks/use-workspace-url';
import { useMemo } from 'react';

interface WorkspaceUrls {
  teamUrl: string;
  cardsUrl: string;
  personalCardsGraphQL: string;
  cardsTeamGraphQLUrl: string;
  presetsUrl: string;
  contactsUrl: string;
  emailSignatureUrl: string;
  accessoriesUrl: string;
  adminUrl: string;
  virtualBackgroundsUrl: string;
  settingsUrl: string;
  workspaceSettingsUrl: string;
  teamMembersUrl: string;
  billingAndSubscriptionUrl: string;
  templatesV3Url: string;
  homeUrl: string;
  integrationsUrl: string;
  newTeamCardUrl: string;
  newTemplateUrl: string;
  integrationCardProvisioning: string;
  integrationCrmSetup: string;
  bulkUploadUrl: string;
  bulkUploadCsvUrl: string;
}

function useWorkspaceUrls(): WorkspaceUrls {
  const generateWorkspaceUrl = useLazyWorkspaceUrl();
  const teamUrl = generateWorkspaceUrl(NavItems.Team);
  const cardsUrl = generateWorkspaceUrl(NavItems.Cards);
  const personalCardsGraphQL = generateWorkspaceUrl(NavItems.PersonalCardsGraphQL);
  const cardsTeamGraphQLUrl = generateWorkspaceUrl(NavItems.CardsTeamGraphQL);
  const presetsUrl = generateWorkspaceUrl(NavItems.Presets);
  const contactsUrl = generateWorkspaceUrl(NavItems.Contacts);
  const emailSignatureUrl = generateWorkspaceUrl(NavItems.EmailSignatures);
  const accessoriesUrl = generateWorkspaceUrl(NavItems.Accessories);
  const adminUrl = generateWorkspaceUrl(NavItems.Admin);
  const virtualBackgroundsUrl = generateWorkspaceUrl(NavItems.VirtualBackgrounds);
  const settingsUrl = useWorkspaceUrl('settings/personal-details');
  const workspaceSettingsUrl = useWorkspaceUrl('settings/workspace');
  const teamMembersUrl = generateWorkspaceUrl(NavItems.TeamMembers);
  const billingAndSubscriptionUrl = generateWorkspaceUrl(`settings/${NavItems.BillingAndSubscription}`);
  const templatesV3Url = generateWorkspaceUrl(NavItems.TemplatesV3);
  const homeUrl = generateWorkspaceUrl(NavItems.Home);
  const integrationsUrl = generateWorkspaceUrl('settings/integrations');
  const integrationCardProvisioning = generateWorkspaceUrl('integrations/card-provisioning');
  const integrationCrmSetup = generateWorkspaceUrl('integrations/export-contacts');
  const newTeamCardUrl = generateWorkspaceUrl(`cards/team/new`);
  const bulkUploadUrl = generateWorkspaceUrl(`cards/team/bulk-upload/select-template`);
  const bulkUploadCsvUrl = generateWorkspaceUrl(`cards/team/bulk-upload/upload-csv`);
  const newTemplateUrl = generateWorkspaceUrl(`cards/templates/new`);

  return useMemo(
    () => ({
      teamUrl,
      cardsUrl,
      personalCardsGraphQL,
      cardsTeamGraphQLUrl,
      presetsUrl,
      contactsUrl,
      emailSignatureUrl,
      accessoriesUrl,
      adminUrl,
      virtualBackgroundsUrl,
      settingsUrl,
      workspaceSettingsUrl,
      teamMembersUrl,
      billingAndSubscriptionUrl,
      templatesV3Url,
      homeUrl,
      integrationsUrl,
      newTeamCardUrl,
      newTemplateUrl,
      bulkUploadUrl,
      bulkUploadCsvUrl,
      integrationCardProvisioning,
      integrationCrmSetup,
    }),
    [
      teamUrl,
      cardsUrl,
      personalCardsGraphQL,
      cardsTeamGraphQLUrl,
      presetsUrl,
      contactsUrl,
      emailSignatureUrl,
      accessoriesUrl,
      adminUrl,
      virtualBackgroundsUrl,
      settingsUrl,
      workspaceSettingsUrl,
      teamMembersUrl,
      billingAndSubscriptionUrl,
      templatesV3Url,
      homeUrl,
      integrationsUrl,
      newTeamCardUrl,
      newTemplateUrl,
      bulkUploadUrl,
      bulkUploadCsvUrl,
      integrationCrmSetup,
      integrationCardProvisioning,
    ]
  );
}

export { useWorkspaceUrls };
